import { memo, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BaseDatePicker, BaseRow, BaseSelect, BaseDateTimePicker, SelectPriority } from '~/components/common';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { catchError, handleApiError, notify } from '~/utils/helper';
import { IEditEventPayload } from '~/utils/interface/schedule';
import { EVENT_COLOR_DEFAULT } from '~/utils/constants/schedules';
import SelectAssignee from '~/components/common/select-assignee/SelectAssignee';
import dayjs from 'dayjs';
import { useAppDispatch } from '~/redux/hooks';
import { scheduleActions } from '~/thunks/schedule/scheduleSlice';
import { handleAddEvent, handleUpdateEvent } from '~/thunks/schedule/scheduleThunk';
import BaseInput from '~/components/common/base-input/BaseInput';

type ConditionalData<T extends 'create' | 'edit'> = T extends 'edit' ? IEditEventPayload : undefined;

type EditEventDialogProps<T extends 'create' | 'edit'> = {
  open: boolean;
  setOpen: (open: boolean, needRefreshData?: boolean) => void;
  mode: T;
  data: ConditionalData<T>;
};
const initialValues: IEditEventPayload = {
  title: '',
  type: '',
  description: '',
  startAt: undefined,
  endAt: undefined,
  priority: '',
  assigned: '',
  subcontractors: '',
  relatedJobs: '',
  relatedContacts: '',
  tags: '',
};

const EditEventDialog = <T extends 'create' | 'edit'>(props: EditEventDialogProps<T>) => {
  //#region Destructuring Props
  const { data, mode = DIALOG_MODE_CREATE } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const [projectsSelection, setProjectsSelection] = useState<Array<any>>([{ id: '1', name: 'None' }, { id: '2', name: 'None' }]);
  const { t } = useTranslation(['admin/schedule', 'admin/common']);
  const dispatch = useAppDispatch();
  const form = useFormik({
    validateOnChange: false, // Prevent form reset API error
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleAddEvent(values)).unwrap();
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }

      if (mode === DIALOG_MODE_EDIT) {
        try {
          if (data?.id) {
            await dispatch(handleUpdateEvent({ id: data.id, data: values })).unwrap();
            notify('success', t('editSuccess', { ns: 'admin/common' }));
            handleSetOpen(false, true);
          }
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    getJobSelections();
  }, []);



  useEffect(() => {
    if (data) {
      form.setValues({
        ...data,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  //#endregion Implement Hook

  //#region Handle Function
  const getJobSelections = async () => {

  };

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    props.setOpen(open, needRefreshData);
  };

  const handleDeleteEvent = () => {
    if (props.data?.id) {
      handleSetOpen(false, false);
      dispatch(scheduleActions.changeSelectedEventId(props.data.id));
      dispatch(scheduleActions.changeOpenAlertDelete(true));
    }
  };

  //#endregion Handle Function

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'
      PaperProps={{
        sx: {
          backgroundColor: "#071427",
          color: "#fff",
        },
      }}>
      <DialogTitle>
        <Typography className='text-[20px] font-[600]'>
          {mode === DIALOG_MODE_CREATE ? "Add New Event" : "Update Event"}
        </Typography>
        <Typography className='text-[14px] text-[#6B7280] font-normal mt-[3px]'>
          Fill in the details below to add new event to Calendar
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <BaseInput name={'title'} title={t('Event Title')} form={form} placeholder={t('Enter Event Title')} inputkey={'title'} type={'1'} />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <BaseInput name={'description'} title={t('Description')} form={form} placeholder={t('Add Description')} inputkey={'description'} type={'1'} />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              {/* <InputLabel>Event Type</InputLabel> */}
              <BaseSelect
                name='type'
                title={t('Event Type')}
                placeholder='Select type'
                value={form.values.type}
                options={projectsSelection}
                onChange={form.handleChange}
                error={Boolean(form.errors.type)}
                helperText={form.errors.type}
              />

            </Box>
            <Box className='w-full'>
              <InputLabel>Start Date & Time</InputLabel>
              <BaseDateTimePicker
                placeholder='Select start date & time'
                value={form.values.startAt}
                onChange={(date) => {
                  if (date) {
                    const selectedDate = dayjs(date).format('YYYY-MM-DD');
                    form.setFieldValue('startAt', selectedDate);
                  }
                }}
                error={Boolean(form.errors.startAt)}
                helperText={form.errors.startAt}
              />
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>End Date & Time</InputLabel>
              <BaseDateTimePicker
                placeholder='Select end date & time'
                value={form.values.endAt}
                onChange={(date) => {
                  if (date) {
                    const selectedDate = dayjs(date).format('YYYY-MM-DD');
                    form.setFieldValue('endAt', selectedDate);

                  }
                }}
                error={Boolean(form.errors.endAt)}
                helperText={form.errors.endAt}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>Priority</InputLabel>
              <BaseSelect
                name='priority'
                placeholder='Select priority'
                value={form.values.priority}
                options={projectsSelection}
                onChange={form.handleChange}
                error={Boolean(form.errors.priority)}
                helperText={form.errors.priority}
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Assigned To</InputLabel>
              <BaseSelect
                name='assigned'
                placeholder='Select assigned to'
                value={form.values.assigned}
                options={projectsSelection}
                onChange={form.handleChange}
                error={Boolean(form.errors.assigned)}
                helperText={form.errors.assigned}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>Subcontractors</InputLabel>
              <BaseSelect
                name='subcontractors'
                placeholder='Select subcontractors'
                value={form.values.subcontractors}
                options={projectsSelection}
                onChange={form.handleChange}
                error={Boolean(form.errors.subcontractors)}
                helperText={form.errors.subcontractors}
              />
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Related Jobs</InputLabel>
              <BaseSelect
                name='relatedJobs'
                placeholder='Select related jobs'
                value={form.values.relatedJobs}
                options={projectsSelection}
                onChange={form.handleChange}
                error={Boolean(form.errors.relatedJobs)}
                helperText={form.errors.relatedJobs}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>Related Contacts</InputLabel>
              <BaseSelect
                name='relatedContacts'
                placeholder='Select related contacts'
                value={form.values.relatedContacts}
                options={projectsSelection}
                onChange={form.handleChange}
                error={Boolean(form.errors.relatedContacts)}
                helperText={form.errors.relatedContacts}
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Tags</InputLabel>
              <BaseSelect
                name='tags'
                placeholder='Select tags'
                value={form.values.tags}
                options={projectsSelection}
                onChange={form.handleChange}
                error={Boolean(form.errors.tags)}
                helperText={form.errors.tags}
              />
            </Box>
            <Box className='w-full'></Box>
          </BaseRow>
        </Box>
      </DialogContent>

      <DialogActions>
        <Box className='cursor-pointer text-[#949699] text-[14px] border border-solid border-[#E5E7EB] py-[11px] px-[16.5px] rounded-[27px]' onClick={() => handleSetOpen(false)}>
          Cancel
        </Box>
        <Box className='cursor-pointer text-[#FFFFFF] bg-[#2D80FC] text-[14px] py-[11px] px-[16.5px] rounded-[27px]' onClick={() => form.handleSubmit()}>
          Save Appointment
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditEventDialog) as typeof EditEventDialog;
